import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ToastHelper } from '../components/common/toaster.helper';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  constructor(private injector: Injector, private zone: NgZone) {}

  handleError(error: any): void {
    // Angular wraps zone errors — unwrap if needed
    const unwrapped = error?.rejection ?? error;

    // Stale deployment: browser cached the old main.js which references chunk hashes
    // that no longer exist on the server after a new build is deployed.
    // Force a hard reload to pick up the new index.html and correct chunk names.
    if (this.isChunkLoadError(unwrapped)) {
      console.warn('[GlobalErrorHandler] Stale chunk detected — reloading to pick up new build.');
      window.location.reload();
      return;
    }

    if (unwrapped instanceof HttpErrorResponse) {
      if (!environment.production) {
        console.error('[GlobalErrorHandler] HTTP error:', unwrapped);
      }
      // Status 0 = network offline or CORS — show toast since no service callback fires
      if (unwrapped.status === 0) {
        this.showError('Network connection issue. Please check your internet connection.');
      }
      // All other HTTP errors (401, 500, etc.) are already handled by
      // service error callbacks — suppress to avoid double-toasting
    } else {
      // Client-side error: template expression, lifecycle hook, JS throw, etc.
      if (!environment.production) {
        console.error('[GlobalErrorHandler] Client error:', unwrapped);
      } else {
        console.error(unwrapped);
      }
      this.showError('An unexpected error occurred. Please refresh the page.');
    }
  }

  private isChunkLoadError(error: any): boolean {
    const msg: string = error?.message ?? '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Loading chunk')
    );
  }

  private showError(message: string): void {
    try {
      const toastr = this.injector.get(ToastHelper);
      this.zone.run(() => toastr.showError(message));
    } catch {
      // ToastHelper unavailable (e.g. error during bootstrap) — fail silently
    }
  }
}
